import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, OverlayTrigger, Tooltip, Dropdown, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function HistoriqueCommandes() {
    const navigate = useNavigate();
    const [commandes, setCommandes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [stats, setStats] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'date_commande', direction: 'desc' });
    const [viewMode, setViewMode] = useState('table');

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // États pour les notifications
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchCommandes();
        fetchStats();
    }, []);

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const fetchCommandes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/commandes_fournisseurs.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setCommandes(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
            showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/commandes_fournisseurs.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { 
                color: 'secondary', 
                label: 'Brouillon', 
                icon: 'ri-draft-line',
                gradient: 'linear-gradient(135deg, #6c757d, #5a6268)'
            },
            envoyee: { 
                color: 'info', 
                label: 'Envoyée', 
                icon: 'ri-send-plane-line',
                gradient: 'linear-gradient(135deg, #0dcaf0, #0b9cbe)'
            },
            recue_partielle: { 
                color: 'warning', 
                label: 'Partielle', 
                icon: 'ri-stack-line',
                gradient: 'linear-gradient(135deg, #f59e0b, #d97706)'
            },
            recue_complete: { 
                color: 'success', 
                label: 'Reçue', 
                icon: 'ri-check-double-line',
                gradient: 'linear-gradient(135deg, #10b981, #059669)'
            },
            annulee: { 
                color: 'danger', 
                label: 'Annulée', 
                icon: 'ri-close-circle-line',
                gradient: 'linear-gradient(135deg, #dc3545, #bb2d3b)'
            }
        };
        const config = configs[statut] || configs.brouillon;
        
        return (
            <Badge 
                className="px-3 py-2 rounded-pill"
                style={{
                    background: config.gradient,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredCommandes = commandes
        .filter(c => {
            const matchesSearch = 
                c.numero_commande?.toLowerCase().includes(filterText.toLowerCase()) ||
                c.fournisseur_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                c.code_fournisseur?.toLowerCase().includes(filterText.toLowerCase());
            
            if (dateDebut && new Date(c.date_commande) < new Date(dateDebut)) return false;
            if (dateFin && new Date(c.date_commande) > new Date(dateFin)) return false;
            
            return matchesSearch;
        })
        .sort((a, b) => {
            if (sortConfig.key === 'date_commande') {
                return sortConfig.direction === 'asc' 
                    ? new Date(a.date_commande) - new Date(b.date_commande)
                    : new Date(b.date_commande) - new Date(a.date_commande);
            }
            if (sortConfig.key === 'montant_ttc') {
                return sortConfig.direction === 'asc' 
                    ? (a.montant_ttc || 0) - (b.montant_ttc || 0)
                    : (b.montant_ttc || 0) - (a.montant_ttc || 0);
            }
            return 0;
        });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCommandes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredCommandes.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    // Calcul des statistiques pour l'en-tête
    const totalCommandes = filteredCommandes.length;
    const totalMontant = filteredCommandes.reduce((sum, cmd) => sum + (cmd.montant_ttc || 0), 0);
    const commandesParStatut = filteredCommandes.reduce((acc, cmd) => {
        acc[cmd.statut] = (acc[cmd.statut] || 0) + 1;
        return acc;
    }, {});

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement de l'historique...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchCommandes}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Notifications */}
                    {showSuccessAlert && (
                        <Alert 
                            variant="success" 
                            onClose={() => setShowSuccessAlert(false)} 
                            dismissible 
                            className="mb-4 alert-modern fade-in"
                            style={{
                                borderRadius: '12px',
                                borderLeft: '4px solid #10b981',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <i className="ri-checkbox-circle-line fs-4 me-3 text-success"></i>
                                <div>
                                    <strong className="text-success">Succès</strong>
                                    <p className="mb-0">{successMessage}</p>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {showErrorAlert && (
                        <Alert 
                            variant="danger" 
                            onClose={() => setShowErrorAlert(false)} 
                            dismissible 
                            className="mb-4 alert-modern fade-in"
                            style={{
                                borderRadius: '12px',
                                borderLeft: '4px solid #dc3545',
                                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                                <div>
                                    <strong className="text-danger">Erreur</strong>
                                    <p className="mb-0">{errorMessage}</p>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(108, 117, 125, 0.15), rgba(90, 98, 104, 0.15))',
                                            boxShadow: '0 10px 20px rgba(108, 117, 125, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-history-line text-secondary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Historique des commandes
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            {totalCommandes} commande(s) au total • {formatCurrency(totalMontant)}
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => navigate('/achats/commandes')}
                                        className="rounded-pill px-4 d-flex align-items-center gap-2"
                                    >
                                        <i className="ri-arrow-left-line"></i>
                                        <span>Retour</span>
                                    </Button>
                                    <Button 
                                        variant="primary" 
                                        onClick={() => navigate('/achats/commandes/nouveau')}
                                        className="rounded-pill px-4 d-flex align-items-center gap-2"
                                        style={{
                                            background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                            border: 'none'
                                        }}
                                    >
                                        <i className="ri-add-line"></i>
                                        <span>Nouvelle commande</span>
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Cartes de statistiques */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-shopping-cart-line text-primary fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block">Total commandes</small>
                                            <h3 className="mb-0 fw-bold">{totalCommandes}</h3>
                                            <small className="text-muted">tous statuts</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-check-double-line text-success fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block">Reçues</small>
                                            <h3 className="mb-0 fw-bold text-success">{commandesParStatut.recue_complete || 0}</h3>
                                            <small className="text-muted">complètes</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-warning bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-stack-line text-warning fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block">Partielles</small>
                                            <h3 className="mb-0 fw-bold text-warning">{commandesParStatut.recue_partielle || 0}</h3>
                                            <small className="text-muted">en attente</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-send-plane-line text-info fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block">Envoyées</small>
                                            <h3 className="mb-0 fw-bold text-info">{commandesParStatut.envoyee || 0}</h3>
                                            <small className="text-muted">en cours</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filtres avancés */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-center">
                                <Col md={4}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <i className="ri-search-line text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher par numéro, fournisseur..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="border-start-0"
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        type="date"
                                        value={dateDebut}
                                        onChange={(e) => setDateDebut(e.target.value)}
                                        className="filter-date"
                                        placeholder="Date début"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        type="date"
                                        value={dateFin}
                                        onChange={(e) => setDateFin(e.target.value)}
                                        className="filter-date"
                                        placeholder="Date fin"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => {
                                            setDateDebut('');
                                            setDateFin('');
                                            setFilterText('');
                                        }}
                                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                                    >
                                        <i className="ri-refresh-line"></i>
                                        <span>Réinitialiser</span>
                                    </Button>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Form.Select 
                                        value={sortConfig.key}
                                        onChange={(e) => setSortConfig({ key: e.target.value, direction: 'desc' })}
                                        className="filter-select"
                                    >
                                        <option value="date_commande">Trier par date</option>
                                        <option value="montant_ttc">Trier par montant</option>
                                    </Form.Select>
                                </Col>
                                <Col md={6} className="d-flex justify-content-end gap-2">
                                    <Button 
                                        variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('table')}
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-table-line me-1"></i>Tableau
                                    </Button>
                                    <Button 
                                        variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('cards')}
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-grid-line me-1"></i>Cartes
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Vue Tableau avec pagination */}
                    {viewMode === 'table' && (
                        <Card className="table-card border-0">
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="mb-0 align-middle custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <SortableHeader field="date_commande">Date</SortableHeader>
                                                <th>N° Commande</th>
                                                <th>Fournisseur</th>
                                                <SortableHeader field="montant_ttc">Montant</SortableHeader>
                                                <th>Statut</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.length > 0 ? (
                                                currentItems.map((cmd, index) => (
                                                    <tr key={cmd.id_commande_fournisseur} style={{
                                                        animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                    }}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="ri-calendar-line text-muted me-2"></i>
                                                                <span>{formatDate(cmd.date_commande)}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Link to={`/achats/commandes/${cmd.id_commande_fournisseur}`} className="text-primary fw-semibold text-decoration-none">
                                                                {cmd.numero_commande}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" 
                                                                     style={{ width: '36px', height: '36px' }}>
                                                                    <i className="ri-store-2-line text-primary"></i>
                                                                </div>
                                                                <div>
                                                                    <Link to={`/achats/fournisseurs/${cmd.id_fournisseur}`} className="text-dark fw-semibold text-decoration-none">
                                                                        {cmd.fournisseur_nom}
                                                                    </Link>
                                                                    <div><small className="text-muted">{cmd.code_fournisseur}</small></div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="fw-bold text-primary">{formatCurrency(cmd.montant_ttc || 0)}</td>
                                                        <td>{getStatutBadge(cmd.statut)}</td>
                                                        <td className="text-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm"
                                                                    className="rounded-circle"
                                                                    style={{ width: '36px', height: '36px' }}
                                                                    onClick={() => navigate(`/achats/commandes/${cmd.id_commande_fournisseur}`)}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-5">
                                                        <div className="empty-state">
                                                            <i className="ri-history-line empty-icon text-secondary" style={{ fontSize: '4rem' }}></i>
                                                            <h5 className="mt-3">Aucune commande dans l'historique</h5>
                                                            <p className="text-muted mb-3">
                                                                {filterText || dateDebut || dateFin 
                                                                    ? "Aucun résultat ne correspond à vos filtres"
                                                                    : "Commencez par créer votre première commande"}
                                                            </p>
                                                            {filterText || dateDebut || dateFin ? (
                                                                <Button 
                                                                    variant="outline-primary"
                                                                    onClick={() => {
                                                                        setFilterText('');
                                                                        setDateDebut('');
                                                                        setDateFin('');
                                                                    }}
                                                                >
                                                                    <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                                </Button>
                                                            ) : (
                                                                <Button 
                                                                    variant="primary"
                                                                    onClick={() => navigate('/achats/commandes/nouveau')}
                                                                >
                                                                    <i className="ri-add-line me-2"></i>Nouvelle commande
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pagination pour la vue tableau */}
                                {totalPages > 1 && (
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                        <div className="text-muted small mb-2 mb-md-0">
                                            <i className="ri-file-list-line me-1"></i>
                                            Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredCommandes.length)} sur {filteredCommandes.length} commandes
                                        </div>
                                        <Pagination className="mb-0 flex-wrap justify-content-center">
                                            <Pagination.First 
                                                onClick={() => handlePageChange(1)}
                                                disabled={currentPage === 1}
                                            />
                                            <Pagination.Prev 
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            />
                                            {renderPagination()}
                                            <Pagination.Next 
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            />
                                            <Pagination.Last 
                                                onClick={() => handlePageChange(totalPages)}
                                                disabled={currentPage === totalPages}
                                            />
                                        </Pagination>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue Cartes avec pagination */}
                    {viewMode === 'cards' && (
                        <>
                            <Row className="g-4">
                                {currentItems.length > 0 ? (
                                    currentItems.map((cmd, index) => (
                                        <Col lg={6} xl={4} key={cmd.id_commande_fournisseur}>
                                            <Card className="border-0 shadow-sm h-100 commande-card" style={{
                                                animation: `fadeIn 0.3s ease ${index * 0.05}s both`,
                                                borderRadius: '16px',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0,0,0,0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
                                            }}>
                                                <Card.Body className="p-4">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div>
                                                            <Badge 
                                                                className="mb-2 px-3 py-2"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                                    border: 'none'
                                                                }}
                                                            >
                                                                {cmd.numero_commande}
                                                            </Badge>
                                                            <h5 className="mb-1">{cmd.fournisseur_nom}</h5>
                                                            <small className="text-muted">{cmd.code_fournisseur}</small>
                                                        </div>
                                                        <div className="text-end">
                                                            <h4 className="text-primary mb-0">{formatCurrency(cmd.montant_ttc || 0)}</h4>
                                                            <small className="text-muted">
                                                                <i className="ri-calendar-line me-1"></i>
                                                                {formatDate(cmd.date_commande)}
                                                            </small>
                                                        </div>
                                                    </div>

                                                    <hr className="my-3" />

                                                    <div className="d-flex justify-content-between align-items-center">
                                                        {getStatutBadge(cmd.statut)}
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                className="rounded-circle"
                                                                style={{ width: '38px', height: '38px' }}
                                                                onClick={() => navigate(`/achats/commandes/${cmd.id_commande_fournisseur}`)}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))
                                ) : (
                                    <Col md={12}>
                                        <Card className="border-0 shadow-sm text-center py-5">
                                            <i className="ri-history-line fs-1 text-muted mb-3"></i>
                                            <h5>Aucune commande dans l'historique</h5>
                                        </Card>
                                    </Col>
                                )}
                            </Row>

                            {/* Pagination pour la vue cartes */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredCommandes.length)} sur {filteredCommandes.length} commandes
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .commande-card {
                        transition: all 0.3s ease;
                    }

                    .commande-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                    }

                    .sortable-header:hover {
                        color: #4361ee;
                    }

                    .search-input .form-control:focus,
                    .filter-date:focus,
                    .filter-select:focus {
                        box-shadow: none;
                        border-color: #4361ee;
                    }

                    .alert-modern {
                        animation: slideInDown 0.3s ease-out;
                        border: none;
                        padding: 1rem 1.5rem;
                    }

                    @keyframes slideInDown {
                        from {
                            transform: translateY(-20px);
                            opacity: 0;
                        }
                        to {
                            transform: translateY(0);
                            opacity: 1;
                        }
                    }

                    .alert-modern .btn-close {
                        padding: 1.2rem 1.5rem;
                    }

                    /* Styles pour la pagination */
                    .pagination {
                        gap: 5px;
                    }
                    
                    .pagination .page-item .page-link {
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                        color: #1e293b;
                        padding: 0.5rem 1rem;
                        transition: all 0.2s ease;
                        font-weight: 500;
                    }
                    
                    .pagination .page-item.active .page-link {
                        background: linear-gradient(135deg, #4361ee, #3a56d4);
                        border-color: #4361ee;
                        color: white;
                        box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                    }
                    
                    .pagination .page-item .page-link:hover {
                        background: #f1f5f9;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    }
                    
                    .pagination .page-item.disabled .page-link {
                        opacity: 0.5;
                        pointer-events: none;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}